import { useIonToast } from '@ionic/react';
import { MutationDefinition } from '@reduxjs/toolkit/dist/query';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useCallback, useEffect, useRef } from 'react';
import { BaseQueryType } from '../api/baseQueryWithReauth.js';

export type Mutation<ArgType, ResultType> = UseMutation<
	MutationDefinition<ArgType, BaseQueryType, string, ResultType, 'BaseApi'>
>;

/*
 * Custom hook to handle image upload, it takes the object the image is attached to,
 * and the mutation function, which should have parms of type
 *   {document, data: buffer, contentType: string}
 * It handles errors by showing a toast, and returns a function that you can either attach
 * to the 'onchange' event of an input element, or the onPaste handler of an input element.
 * It will handle both the file upload events and a clipboard paste event properly.
 */
export function useImageUpload<ArgType, ResultType>(
	document: ResultType | null | undefined,
	useAddImage: Mutation<ArgType, ResultType>
): { loading: boolean; uploadImage: (e: any) => Promise<void>; response?: ResultType } {
	const [showToast] = useIonToast();
	const [trigger, { error, isLoading: loading, data: response }] = useAddImage();
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	useEffect(() => {
		if (error) {
			if ('status' in error && error.status === 403) {
				showToast({
					duration: 1500,
					position: 'top',
					color: 'danger',
					message: 'You are not authorized to change this image'
				});
			} else {
				showToast({
					duration: 1500,
					position: 'top',
					color: 'danger',
					message: 'Please try a different image'
				});
			}
		}
		if (!loading && timeoutRef.current !== undefined) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}
	}, [error, loading, showToast]);

	const uploadImage = useCallback(
		async (e: any) => {
			const file: Blob = e?.target?.files?.[0] || e?.clipboardData?.files?.[0];

			if (file && document) {
				const reader = new FileReader();

				reader.onload = (event: any) => {
					const [, base64] = event.currentTarget.result.split(',');
					trigger({
						document,
						data: { image: base64, contentType: file.type }
					} as unknown as ArgType);
				};

				reader.readAsDataURL(file);
			}
		},
		[document, trigger]
	);

	return { loading, uploadImage, response };
}
